/* ═══════════════════════════════════════════════════════════════
   The Designer — the paper itself.

   Page geometry comes from the ONE body-layout brain; the header,
   footer, pre-body and post-body bands come from the ONE zone-layout
   brain (shared/zone-layout.ts) with the same baked font metrics the
   Word export measures with. So the bands wrap and pair here exactly
   as they will in the .docx.

   R1 renders one continuous sheet with the real page width, real
   margins and the bands in place. Measured page-break bands are a
   later phase — per the contract, pagination cosmetics never block
   the editing surface.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { X } from 'lucide-react';
import type { StandardConfig } from '../../shared/types';
import { layoutZonesIndexed, type TokenTextCtx, type ZoneRow } from '../../shared/zone-layout';
import { placeholderId, isNumberedPlaceholder } from '../../shared/forma-placeholders';
import { makeZoneMeasure } from '../lib/zone-measure';
import { resolvePageGeometry, inToPx, ptToPx } from '../../shared/body-layout';
import {
  tokBase,
  tokModStyle,
  tokSpacing,
  parseLineParams,
  parseImageId,
  applyCaseModText,
} from '../utils';
import { ALL_TOKENS } from '../constants';
import {
  CanvasSelectionContext,
  TokenClipboardContext,
  TokenFormatMenu,
  TokenSlotMenu,
  slotClipboardItems,
  type TokenMenuItem,
} from '../components/TokenComponents';
import { COLUMN_LABEL, zoneRemoveAt, zoneUpdateAt } from './formaZonesModel';
import type { TextDialogRequest } from './DesignPalettes';

export interface PaperContext {
  forma: StandardConfig | null;
  specNumber: string;
  specName: string;
  /** Project/client/issuance values + resolved placeholders, already merged. */
  projectMeta: Record<string, string>;
}

/** Format raw CSI digits per the forma's spec-number pattern ('## ## ##'). */
export function formatSpecNumber(raw: string, pattern?: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (!pattern || !digits) return raw || '';
  let i = 0;
  const out = pattern.replace(/#/g, () => digits[i++] || '');
  return i < digits.length ? out + digits.slice(i) : out;
}

function formatDate(format?: string): string {
  const d = new Date();
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yyyy = d.getFullYear();
  switch (format) {
    case 'MMMM d, yyyy':
      return `${d.toLocaleDateString('en-US', { month: 'long' })} ${dd}, ${yyyy}`;
    case 'MM/dd/yyyy':
      return `${String(mm).padStart(2, '0')}/${String(dd).padStart(2, '0')}/${yyyy}`;
    case 'yyyy-MM-dd':
      return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    default:
      return `${mm}/${dd}/${yyyy}`;
  }
}

export type ZoneKind = 'header' | 'footer' | 'pre' | 'post';

interface ZoneBandProps extends PaperContext {
  kind: ZoneKind;
  /** Page number to resolve {Page} with. R1 renders one continuous sheet. */
  pageNum?: number;
  numPages?: number;
  /** Design mode: force which page variant's zones render (the variant being
      edited), instead of deriving it from pageNum. Additive — omitted, the
      Write-mode behaviour is exactly as before. */
  variant?: 'First' | 'Odd' | 'Even';
  /** Live editing surface (the Studio's canvas) — a token becomes a fully
   *  clickable/draggable/selectable/droppable object (CanvasSelectionContext,
   *  DESIGN.md §4's copy/cut/paste triad). The sheet is the ONLY place a
   *  master forma's zones are worked now; the "Header & Footer" panel is
   *  retired (Josh, 2026-08-13), so everything it did happens here. Omitted
   *  or false — every other caller (read-only viewers, export preview)
   *  renders exactly as before: plain, inert text. */
  interactive?: boolean;
  /** Only consulted when `interactive` — commits the on-sheet context
   *  menu's Delete/Edit-text/format actions as ordinary undoable forma
   *  edits (contract §2.7). Omitted → Delete/Edit are unavailable (a
   *  read-only or non-edit caller never reaches here anyway). */
  edit?: (fn: (f: StandardConfig) => StandardConfig) => void;
  /** Opens the shared literal-text editor (TextTokenDialog) — used by the
   *  on-sheet context menu's "Edit text" action and by clicking an empty
   *  band to add the first line of text (Josh, 2026-08-13, Order 1). */
  onRequestTextEdit?: (req: TextDialogRequest) => void;
  /** Drop a token at an EXACT position in a zone (before/after another token,
   *  optionally starting a new line) — the classic canvas's insertAtInZone,
   *  restored on the sheet. Omitted → a drop onto a token does nothing and
   *  the band's append-only drop (ZoneShell) is the only path, exactly as
   *  every non-Studio caller behaves today. */
  onZoneInsertAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null,
    newLine: boolean
  ) => void;
  /** Drop a token ONTO another to replace it — the classic replaceAtInZone. */
  onZoneReplaceAt?: (
    zoneKey: string,
    idx: number,
    value: string,
    srcZone: string | null,
    srcIdx: number | null
  ) => void;
}

/** Which edge of a token a drag is over — the SAME five-way split the panel's
 *  ZoneChip has always used (TokenComponents.tsx), so a drop means the same
 *  thing on the sheet as it does in the panel. */
type DropSide = 'center' | 'top' | 'bottom' | 'left' | 'right';

/** L/C/R — the three columns of a band, as the layout brain keys them. */
type ZoneColumnKey = 'L' | 'C' | 'R';

function dropSideOf(e: React.DragEvent, rect: DOMRect): DropSide {
  const x = (e.clientX - rect.left) / Math.max(1, rect.width);
  const y = (e.clientY - rect.top) / Math.max(1, rect.height);
  if (x > 0.3 && x < 0.7 && y > 0.25 && y < 0.75) return 'center';
  const dists: Record<string, number> = { top: y, bottom: 1 - y, left: x, right: 1 - x };
  return Object.entries(dists).sort((a, b) => a[1] - b[1])[0][0] as DropSide;
}

function isCustomTokenValue(tok: string): boolean {
  if (tok === '\n') return false;
  const base = tokBase(tok);
  if (base.startsWith('{Image:')) return false;
  if (base === '{EndOfSection}') return false;
  if (base.startsWith('{ProjectToken:')) return false;
  if (isNumberedPlaceholder(base)) return false;
  if (ALL_TOKENS.find((x) => x.value === base)) return false;
  return true;
}

/**
 * One L/C/R band, laid out by the shared zone brain and rendered as rows that
 * are GUARANTEED to fit side by side — collision is structurally impossible
 * because the layout decided it, not CSS.
 */
export function ZoneBand({
  kind,
  forma,
  specNumber,
  specName,
  projectMeta,
  pageNum = 1,
  numPages = 1,
  variant: variantOverride,
  interactive,
  edit,
  onRequestTextEdit,
  onZoneInsertAt,
  onZoneReplaceAt,
}: ZoneBandProps) {
  const canvasSel = React.useContext(CanvasSelectionContext);
  const clip = React.useContext(TokenClipboardContext);
  /** Which token edge the airborne chip is over — drives the insertion caret. */
  const [dropHint, setDropHint] = React.useState<{ key: string; side: DropSide } | null>(null);
  /** Right-click menu for an L/C/R column region, or for a line-break marker. */
  const [slotMenu, setSlotMenu] = React.useState<{
    x: number;
    y: number;
    label: string;
    items: TokenMenuItem[];
  } | null>(null);
  // Anchors for the on-sheet context menu (TokenFormatMenu positions off the
  // real DOM node) — keyed by `token:<zoneKey>:<idx>`, refreshed every render.
  const spanRefs = React.useRef<Record<string, HTMLElement | null>>({});
  const doc = (forma?.docSettings || {}) as Record<string, unknown>;
  const hf = (forma?.headerFooter || {}) as Record<string, string[] | undefined>;
  const sp = (forma?.special || {}) as Record<string, unknown>;
  const images =
    (
      forma as unknown as {
        images?: Record<string, { width: number; height: number; mimeType: string; data: string }>;
      }
    )?.images || {};

  const specNumFmt = formatSpecNumber(specNumber, doc.specNumberFormat as string | undefined);
  // AS-TYPED, never force-uppercased: the export brain (shared/zone-layout
  // tokenText + word-engine) prints {SpecName} exactly as entered — only the
  // `caps` MOD uppercases, and tokModStyle already renders that mod as CSS
  // text-transform. The paper's old unconditional toUpperCase showed caps the
  // .docx would never print (Josh, 2026-08-17).
  const specNameTxt = specName || '';
  const geo = resolvePageGeometry(forma);

  const zones = React.useMemo(() => {
    if (kind === 'pre') {
      return {
        L: sp.bodyTopLeft as string[] | undefined,
        C: sp.bodyTopCenter as string[] | undefined,
        R: sp.bodyTopRight as string[] | undefined,
        keyL: 'bodyTopLeft',
        keyC: 'bodyTopCenter',
        keyR: 'bodyTopRight',
      };
    }
    if (kind === 'post') {
      if (sp.postBodyEnabled === false)
        return { L: [], C: [], R: [], keyL: 'postLeft', keyC: 'postCenter', keyR: 'postRight' };
      return {
        L: sp.postLeft as string[] | undefined,
        C: sp.postCenter as string[] | undefined,
        R: sp.postRight as string[] | undefined,
        keyL: 'postLeft',
        keyC: 'postCenter',
        keyR: 'postRight',
      };
    }
    // Word's own rule: page 1 only gets its own band when the forma asked for
    // a different first page. Otherwise page 1 prints the Odd band like every
    // other page — a forma with `firstPageDifferent: false` and an empty First
    // band must NOT render a blank header here.
    const variant =
      variantOverride ??
      (pageNum === 1 && geo.firstPageDifferent
        ? 'First'
        : geo.oddEvenDifferent && pageNum % 2 === 0
          ? 'Even'
          : 'Odd');
    return {
      L: hf[`${kind}${variant}Left`],
      C: hf[`${kind}${variant}Center`],
      R: hf[`${kind}${variant}Right`],
      keyL: `${kind}${variant}Left`,
      keyC: `${kind}${variant}Center`,
      keyR: `${kind}${variant}Right`,
    };
  }, [kind, hf, sp, pageNum, geo.oddEvenDifferent, geo.firstPageDifferent, variantOverride]);

  const ctx: TokenTextCtx = React.useMemo(
    () => ({
      specNumber: specNumFmt,
      specName: specNameTxt,
      endOfSectionText: sp.endOfSectionText as string | undefined,
      dateFormat: doc.dateFormat as string | undefined,
      project: projectMeta,
      placeholderMemories: Object.fromEntries(
        Object.entries(
          (forma as unknown as { placeholders?: Record<string, { was?: string }> })?.placeholders ||
            {}
        ).map(([id, p]) => [id, p?.was || ''])
      ),
    }),
    [specNumFmt, specNameTxt, sp.endOfSectionText, doc.dateFormat, projectMeta, forma]
  );

  const measure = React.useMemo(
    () =>
      makeZoneMeasure({
        sizePt: geo.bodyFontSizePt,
        family: geo.bodyFont,
        ctx,
        images,
      }),
    [geo.bodyFontSizePt, geo.bodyFont, ctx, images]
  );

  const resolveToken = (tok: string, key: number): React.ReactNode => {
    if (tok === '\n') return null;
    const base = tokBase(tok);
    const style = tokModStyle(tok) as React.CSSProperties;
    // A resolved token value can be multi-line (project tokens routinely are:
    // "Client Name\nCity, State"). The layout brain measures such a value by
    // its WIDEST line, so the paper must break it into lines too — rendered
    // as one long line it overruns its column and collides with its neighbour.
    const span = (rawTxt: string) => {
      // titlecase/sentencecase need the real string transform — CSS has no
      // sentence-case value, and `capitalize` doesn't lowercase source text
      // that's already stored ALL CAPS. `caps` stays CSS-driven via `style`.
      const txt = applyCaseModText(String(rawTxt), tok);
      const lines = txt.split('\n');
      if (lines.length === 1) {
        return (
          <span key={key} style={style}>
            {txt}
          </span>
        );
      }
      return (
        <span key={key} style={style}>
          {lines.map((ln, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {ln}
            </React.Fragment>
          ))}
        </span>
      );
    };
    switch (base) {
      case '{SpecNumber}':
        return span(specNumFmt);
      case '{SpecName}':
        return span(specNameTxt);
      case '{SpecNumName}':
        return span(`${specNumFmt} - ${specNameTxt}`);
      case '{Page}':
        return span(String(pageNum));
      case '{NumPages}':
        return span(String(numPages));
      case '{PageOfPages}':
        return span(`Page ${pageNum} of ${numPages}`);
      case '{Date}':
        return span(formatDate(doc.dateFormat as string | undefined));
      case '{Space}':
        return span(' ');
      default: {
        if (base.startsWith('{Image:')) {
          const id = parseImageId(tok);
          const img = id ? images[id] : null;
          if (!img) return null;
          const maxH = 42;
          return (
            <img
              key={key}
              src={`data:${img.mimeType};base64,${img.data}`}
              alt=""
              style={{ height: maxH, width: Math.round(img.width * (maxH / img.height)) }}
            />
          );
        }
        const phId = placeholderId(base);
        if (phId) {
          const val = projectMeta[phId] || '';
          if (val) return span(val);
          const was = (forma as unknown as { placeholders?: Record<string, { was?: string }> })
            ?.placeholders?.[phId]?.was;
          return (
            <span key={key} style={{ ...style }} className="dsg-token-unbound">
              {applyCaseModText(was || `‹${phId}›`, tok)}
            </span>
          );
        }
        if (base.startsWith('{ProjectToken:')) {
          const val = projectMeta[base.slice(1, -1)] || '';
          return val ? (
            span(val)
          ) : (
            <span key={key} className="dsg-token-unbound">
              ‹Project token›
            </span>
          );
        }
        const m = base.match(
          /^\{((?:Project|Client|Issuance|Team|Deliverable)(?:\.[A-Za-z]+)?)\}$/
        );
        if (m) {
          const val = projectMeta[m[1]] || '';
          return val ? (
            span(val)
          ) : (
            <span key={key} className="dsg-token-unbound">{`‹${m[1]}›`}</span>
          );
        }
        return span(base);
      }
    }
  };

  const noLines = (t?: string[]) => (t || []).filter((v) => !v.startsWith('{Line'));

  // ─── On-sheet direct manipulation (Josh, 2026-08-13): a token rendered on
  // the paper is the SAME selectable/draggable object the panel's ZoneChip
  // is — same `token:<zoneKey>:<idx>` key, same CanvasSelectionContext, so
  // copy/cut/paste/Delete (DESIGN.md §4) and cross-band drag work the
  // moment a token is wired in. `layoutZonesIndexed` (the same wrap/pack/zip
  // brain as `layoutZones`, additive) hands back each rendered fragment's
  // ORIGIN INDEX directly — no more matching by value, so a literal that
  // word-wraps across several visual lines stays addressable as its ONE
  // source token on every fragment (the gap the classic value-matching hack
  // couldn't close). That index is local to the `{Line}`-filtered array fed
  // to the layout brain; `rawMap` below translates it back to the position
  // in the REAL zone array (`readZone`'s indexing) that a `{Line}` rule
  // token — filtered out of layout, never gone from storage — would
  // otherwise throw off.
  const zoneKeyOf = (col: 'L' | 'C' | 'R') =>
    col === 'L' ? zones.keyL : col === 'C' ? zones.keyC : zones.keyR;
  const rawArrOf = (col: 'L' | 'C' | 'R'): string[] =>
    (col === 'L' ? zones.L : col === 'C' ? zones.C : zones.R) || [];
  const rawIndexMap = (col: 'L' | 'C' | 'R'): number[] => {
    const map: number[] = [];
    rawArrOf(col).forEach((v, i) => {
      if (!v.startsWith('{Line')) map.push(i);
    });
    return map;
  };
  const rawMapL = rawIndexMap('L');
  const rawMapC = rawIndexMap('C');
  const rawMapR = rawIndexMap('R');
  const rawMapOf = (col: 'L' | 'C' | 'R') =>
    col === 'L' ? rawMapL : col === 'C' ? rawMapC : rawMapR;

  const widthTwips = Math.max(1440, Math.round(geo.contentWidthIn * 1440));
  const { rows, rowsSrc } = layoutZonesIndexed(
    noLines(zones.L),
    noLines(zones.C),
    noLines(zones.R),
    widthTwips,
    measure
  );

  // Each visual row's origin indices, translated back into RAW zone-array
  // space. A literal that WRAPS occupies several rows carrying the SAME
  // origin index — which is exactly why a line-break marker can't be derived
  // per fragment; it has to be derived from the gap BETWEEN two rows' raw
  // indices (see breaksAroundRow).
  const rowRawIdxs = (col: ZoneColumnKey): number[][] => {
    const rawMap = rawMapOf(col);
    return rowsSrc.map((s) =>
      s[col]
        .map((v) => (v != null ? (rawMap[v] ?? null) : null))
        .filter((v): v is number => v != null)
    );
  };
  const rowRawL = rowRawIdxs('L');
  const rowRawC = rowRawIdxs('C');
  const rowRawR = rowRawIdxs('R');
  const rowRawOf = (col: ZoneColumnKey) =>
    col === 'L' ? rowRawL : col === 'C' ? rowRawC : rowRawR;

  /** The raw `'\n'` indices that CLOSE a column's visual row (rendered as a ↵
   *  at the end of it) and — for the column's first row with content — the
   *  ones that OPEN it. A `'\n'` is consumed by the layout brain as a line
   *  splitter and never reaches a row, so without this it is invisible on the
   *  paper AND impossible to delete: exactly the functionality the classic
   *  canvas's DropZone had and the sheet lost. */
  const breaksAroundRow = (col: ZoneColumnKey, ri: number): { open: number[]; close: number[] } => {
    const raw = rawArrOf(col);
    const perRow = rowRawOf(col);
    const cur = perRow[ri] || [];
    if (!cur.length) return { open: [], close: [] };
    const lastSrc = Math.max(...cur);
    let nextFirst = raw.length;
    for (let k = ri + 1; k < perRow.length; k++) {
      if (perRow[k].length) {
        nextFirst = Math.min(...perRow[k]);
        break;
      }
    }
    const close: number[] = [];
    for (let i = lastSrc + 1; i < nextFirst && i < raw.length; i++) {
      if (raw[i] === '\n') close.push(i);
    }
    const open: number[] = [];
    if (perRow.findIndex((r) => r.length > 0) === ri) {
      const firstSrc = Math.min(...cur);
      for (let i = 0; i < firstSrc; i++) if (raw[i] === '\n') open.push(i);
    }
    return { open, close };
  };

  const openTokenEdit = (zoneKey: string, idx: number, raw: string) => {
    if (!onRequestTextEdit || !edit) return;
    if (raw.startsWith('{') && raw.endsWith('}')) return; // fields aren't literal text
    const pipe = raw.lastIndexOf('|');
    const base = pipe > 0 ? raw.slice(0, pipe) : raw;
    const suffix = pipe > 0 ? raw.slice(pipe) : '';
    onRequestTextEdit({
      title: 'Edit text',
      initial: base,
      onCommit: (text) => edit((f) => zoneUpdateAt(f, zoneKey, idx, text + suffix)),
    });
  };

  // A drop ONTO a token places it exactly — before, after, on a new line
  // above/below, or replacing it. Same five-way split, same meaning, as the
  // panel's ZoneChip; without it every drop appended to the end of the column
  // and reordering inside a column was impossible.
  const canDropOnToken = !!(onZoneInsertAt || onZoneReplaceAt);

  const handleTokenDrop = (e: React.DragEvent, zoneKey: string, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const side = dropSideOf(e, (e.currentTarget as HTMLElement).getBoundingClientRect());
    setDropHint(null);
    let d: {
      type?: string;
      value?: string;
      sourceZone?: string | null;
      sourceIndex?: number | null;
      copy?: boolean;
      keys?: string[];
    } | null = null;
    try {
      d = JSON.parse(e.dataTransfer.getData('application/json'));
    } catch {
      return;
    }
    if (!d) return;
    // A whole multi-selection, or a Ctrl-drag copy → the shared move path.
    if (d.type === 'tokenMulti' && d.keys?.length) {
      canvasSel.moveTokens(zoneKey, d.keys, !!d.copy);
      return;
    }
    if (d.type === 'token' && d.copy && d.sourceZone != null && d.sourceIndex != null) {
      canvasSel.moveTokens(zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true);
      return;
    }
    if (d.value == null) return;
    const srcZone = d.sourceZone ?? null;
    const srcIdx = d.sourceIndex ?? null;
    if (side === 'center') {
      onZoneReplaceAt?.(zoneKey, idx, d.value, srcZone, srcIdx);
      return;
    }
    const after = side === 'bottom' || side === 'right';
    const newLine = side === 'top' || side === 'bottom';
    onZoneInsertAt?.(zoneKey, after ? idx + 1 : idx, d.value, srcZone, srcIdx, newLine);
  };

  /** A `'\n'` rendered as a real, selectable object on the paper. */
  const breakMarker = (col: ZoneColumnKey, rawIdx: number) => {
    const zoneKey = zoneKeyOf(col);
    const key = `token:${zoneKey}:${rawIdx}`;
    const selected = canvasSel.selectedTokens.includes(key);
    return (
      <span
        key={`br${rawIdx}`}
        data-token-key={key}
        role="button"
        tabIndex={0}
        title="Line break"
        aria-label="Line break — select and press Delete, or right-click to remove"
        // Selection is announced, not just tinted — colour is never the only
        // signal for state (DESIGN.md §7).
        aria-pressed={selected}
        className={`dsg-token-break${selected ? ' is-selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          canvasSel.select(key, { additive: e.ctrlKey || e.metaKey });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          canvasSel.select(key);
          setSlotMenu({
            x: e.clientX,
            y: e.clientY,
            label: 'Line break actions',
            items: [
              {
                label: 'Delete line break',
                enabled: !!edit,
                danger: true,
                run: () => {
                  edit?.((f) => zoneRemoveAt(f, zoneKey, rawIdx));
                  canvasSel.select(null);
                },
              },
              ...(clip ? slotClipboardItems(clip, zoneKey, rawArrOf(col).length > 0) : []),
            ],
          });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            canvasSel.select(key);
          }
        }}
      >
        ↵
        {/* The same inline × every token carries (Josh, 2026-08-17: "the
            return doesn't have an x either") — one removal language. */}
        {edit && (
          <button
            type="button"
            className="dsg-token-x"
            title="Remove line break"
            aria-label="Remove line break"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              edit((f) => zoneRemoveAt(f, zoneKey, rawIdx));
              canvasSel.select(null);
            }}
          >
            <X size={10} strokeWidth={2.2} aria-hidden />
          </button>
        )}
      </span>
    );
  };

  const renderZone = (
    tokens: string[],
    col: ZoneColumnKey,
    origIdxs: (number | null)[],
    rowIdx: number
  ) => {
    const nodes: React.ReactNode[] = [];
    const zoneKey = zoneKeyOf(col);
    const rawArr = rawArrOf(col);
    const rawMap = rawMapOf(col);
    const breaks = interactive ? breaksAroundRow(col, rowIdx) : { open: [], close: [] };
    breaks.open.forEach((nl) => nodes.push(breakMarker(col, nl)));
    tokens.forEach((t, i) => {
      const node = resolveToken(t, i);
      if (node == null) return;
      const prev = i > 0 ? tokens[i - 1] : null;
      const needSpace =
        nodes.length > 0 &&
        prev !== '\n' &&
        tokBase(t) !== '{Space}' &&
        prev &&
        tokBase(prev) !== '{Space}';
      const filteredIdx = interactive ? (origIdxs[i] ?? null) : null;
      const origIdx = filteredIdx != null ? (rawMap[filteredIdx] ?? null) : null;
      const myKey = origIdx != null ? `token:${zoneKey}:${origIdx}` : null;
      const rawValue = origIdx != null ? (rawArr[origIdx] ?? t) : t;
      const selected = !!myKey && canvasSel.selectedTokens.includes(myKey);
      const menuOpen = !!myKey && canvasSel.menuKey === myKey;
      const hintSide = myKey && dropHint?.key === myKey ? dropHint.side : null;
      // A literal that word-wraps paints as SEVERAL fragments (one per visual
      // row), all under this ONE origin index. They must read as ONE object
      // (Josh, 2026-08-13: "one token looks like 3 different tokens"): the
      // fragment position drives continuous styling (rounded only at the
      // ends), hover on any fragment lights every kin fragment, and exactly
      // one — the last — carries the inline × remove.
      const perRow = rowRawOf(col);
      const fragRows: number[] = [];
      if (origIdx != null)
        perRow.forEach((r, k) => {
          if (r.includes(origIdx)) fragRows.push(k);
        });
      const fragPos =
        fragRows.length <= 1
          ? 'only'
          : rowIdx === fragRows[0]
            ? 'first'
            : rowIdx === fragRows[fragRows.length - 1]
              ? 'last'
              : 'mid';
      const kinHover = (on: boolean) => {
        if (!myKey) return;
        document
          .querySelectorAll(`[data-token-key="${myKey}"]`)
          .forEach((el) => el.classList.toggle('is-kin-hover', on));
      };
      const wrapped = myKey ? (
        <span
          key={i}
          ref={(el) => {
            spanRefs.current[myKey] = el;
          }}
          data-token-key={myKey}
          draggable
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          aria-label={`${tokBase(t)} — click to select, drag to move, drop a token on an edge to place it, right-click to format`}
          className={[
            'dsg-ptoken',
            `is-frag-${fragPos}`,
            selected ? 'is-selected' : '',
            hintSide === 'center' ? 'is-drop-center' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseEnter={() => kinHover(true)}
          onMouseLeave={() => kinHover(false)}
          onDragOver={
            canDropOnToken
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const side = dropSideOf(
                    e,
                    (e.currentTarget as HTMLElement).getBoundingClientRect()
                  );
                  setDropHint((h) =>
                    h?.key === myKey && h.side === side ? h : { key: myKey, side }
                  );
                }
              : undefined
          }
          onDragLeave={
            canDropOnToken
              ? (e) => {
                  if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
                  setDropHint((h) => (h?.key === myKey ? null : h));
                }
              : undefined
          }
          onDrop={
            canDropOnToken ? (e) => handleTokenDrop(e, zoneKey, origIdx as number) : undefined
          }
          onDragEnd={() => setDropHint(null)}
          onDragStart={(e) => {
            const copy = e.ctrlKey || e.metaKey;
            e.dataTransfer.setData(
              'application/json',
              JSON.stringify({
                type: 'token',
                value: rawValue,
                sourceZone: zoneKey,
                sourceIndex: origIdx,
                copy,
              })
            );
            e.dataTransfer.effectAllowed = copy ? 'copy' : 'copyMove';
          }}
          onClick={(e) => {
            canvasSel.select(myKey, { additive: e.ctrlKey || e.metaKey });
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            canvasSel.select(myKey, { menu: true, x: e.clientX, y: e.clientY });
          }}
          onDoubleClick={(e) => {
            // Same shortcut the panel's ZoneChip offered: double-click a
            // literal-text token to edit it directly, no menu detour.
            if (!onRequestTextEdit || !isCustomTokenValue(rawValue)) return;
            e.preventDefault();
            e.stopPropagation();
            canvasSel.select(myKey);
            openTokenEdit(zoneKey, origIdx as number, rawValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              canvasSel.select(myKey);
            }
          }}
        >
          {node}
          {/* Inline remove — visible on hover/selection, no keyboard or
              right-click detour needed (Josh, 2026-08-13). Last fragment
              only, so a wrapped literal offers exactly ONE ×. */}
          {edit && (fragPos === 'only' || fragPos === 'last') && (
            <button
              type="button"
              className="dsg-token-x"
              title="Remove"
              aria-label={`Remove ${tokBase(t)}`}
              draggable={false}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                kinHover(false);
                edit((f) => zoneRemoveAt(f, zoneKey, origIdx as number));
                canvasSel.select(null);
              }}
            >
              <X size={10} strokeWidth={2.2} aria-hidden />
            </button>
          )}
          {hintSide && hintSide !== 'center' && (
            <span className={`dsg-tokendrop dsg-tokendrop-${hintSide}`} aria-hidden="true" />
          )}
          {menuOpen && (
            <TokenFormatMenu
              value={rawValue}
              myKey={myKey}
              isCustom={isCustomTokenValue(rawValue)}
              sizeAware
              onEdit={
                onRequestTextEdit && isCustomTokenValue(rawValue)
                  ? () => openTokenEdit(zoneKey, origIdx as number, rawValue)
                  : undefined
              }
              onRemove={
                edit ? () => edit((f) => zoneRemoveAt(f, zoneKey, origIdx as number)) : undefined
              }
              onInsertLineBreak={
                onZoneInsertAt
                  ? () => onZoneInsertAt(zoneKey, (origIdx as number) + 1, '\n', null, null, false)
                  : undefined
              }
              onSetFixedDate={
                onRequestTextEdit && edit && tokBase(rawValue) === '{Date}'
                  ? () =>
                      onRequestTextEdit({
                        title: 'Use a fixed date',
                        initial: new Date().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }),
                        datePicker: true,
                        onCommit: (text) =>
                          edit((f) => zoneUpdateAt(f, zoneKey, origIdx as number, text)),
                      })
                  : undefined
              }
              anchorRef={{ current: spanRefs.current[myKey] ?? null }}
            />
          )}
        </span>
      ) : (
        node
      );
      nodes.push(
        <React.Fragment key={i}>
          {needSpace ? ' ' : ''}
          {wrapped}
        </React.Fragment>
      );
    });
    breaks.close.forEach((nl) => nodes.push(breakMarker(col, nl)));
    return nodes.length ? nodes : ' ';
  };

  const lineTok = [...(zones.L || []), ...(zones.C || []), ...(zones.R || [])].find((t) =>
    t.startsWith('{Line')
  );
  const rule = lineTok ? (
    <div
      className="dsg-zone-rule"
      style={{
        borderBottomWidth: Math.max(1, Math.round(parseLineParams(lineTok).thickness)),
        borderBottomColor: `#${parseLineParams(lineTok).color}`,
      }}
    />
  ) : null;

  if (!rows.length && !rule) return null;

  const rowSpacing = (row: ZoneRow): React.CSSProperties => {
    let before = 0;
    let after = 0;
    [...row.L, ...row.C, ...row.R].forEach((t) => {
      const s = tokSpacing(t);
      if (s.before > before) before = s.before;
      if (s.after > after) after = s.after;
    });
    return { marginTop: ptToPx(before), marginBottom: ptToPx(after) };
  };

  const ruleBelow = kind === 'header' || kind === 'pre';

  // ─── The three column regions (Josh, 2026-08-13) ───
  // Clicking a column TARGETS it: the palette's click-to-add lands there, the
  // copy/cut/paste triad addresses it by `slot:<zoneKey>`, and right-click
  // gives it the same Paste / Copy all / Cut all menu the panel's DropZone
  // has. They sit UNDER the token spans (z-index 0 vs 1), so a click on a
  // token still selects the token; they carry no drag handlers, so ZoneShell's
  // own drop targets keep working by bubbling.
  const columnRegions = interactive ? (
    <div className="dsg-zonecols">
      {(['L', 'C', 'R'] as ZoneColumnKey[]).map((c) => {
        const zk = zoneKeyOf(c);
        const targeted = canvasSel.selKey === `slot:${zk}`;
        return (
          <div
            key={c}
            className={`dsg-zonecol${targeted ? ' is-targeted' : ''}`}
            role="button"
            tabIndex={0}
            aria-pressed={targeted}
            aria-label={`${COLUMN_LABEL[c]} column of the ${kind} band — click to target it`}
            onClick={() => canvasSel.select(`slot:${zk}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                canvasSel.select(`slot:${zk}`);
              }
            }}
            onContextMenu={(e) => {
              if (!clip) return;
              e.preventDefault();
              e.stopPropagation();
              canvasSel.select(`slot:${zk}`);
              setSlotMenu({
                x: e.clientX,
                y: e.clientY,
                label: `${COLUMN_LABEL[c]} column actions`,
                items: slotClipboardItems(clip, zk, rawArrOf(c).length > 0),
              });
            }}
          />
        );
      })}
    </div>
  ) : null;

  return (
    <div className={`dsg-zone dsg-zone-${kind}`} aria-label={`${kind} band`}>
      {columnRegions}
      {!ruleBelow && rule}
      {rows.map((row, ri) => {
        const occupied = [row.L, row.C, row.R].filter((z) => z.length > 0);
        const spacing = rowSpacing(row);
        const src = rowsSrc[ri];
        if (occupied.length === 0)
          return (
            <div key={ri} style={spacing}>
              &nbsp;
            </div>
          );
        if (occupied.length === 1) {
          const align = row.C.length ? 'center' : row.R.length ? 'right' : 'left';
          const col: ZoneColumnKey = row.L.length ? 'L' : row.C.length ? 'C' : 'R';
          return (
            <div key={ri} style={{ ...spacing, textAlign: align }}>
              {renderZone(row[col], col, src[col], ri)}
            </div>
          );
        }
        if (row.C.length > 0) {
          return (
            <div key={ri} style={{ ...spacing, position: 'relative' }}>
              <div style={{ textAlign: 'center', minHeight: '1em' }}>
                {renderZone(row.C, 'C', src.C, ri)}
              </div>
              {row.L.length > 0 && (
                <div style={{ position: 'absolute', left: 0, top: 0 }}>
                  {renderZone(row.L, 'L', src.L, ri)}
                </div>
              )}
              {row.R.length > 0 && (
                <div style={{ position: 'absolute', right: 0, top: 0 }}>
                  {renderZone(row.R, 'R', src.R, ri)}
                </div>
              )}
            </div>
          );
        }
        return (
          <div key={ri} style={{ ...spacing, display: 'flex', justifyContent: 'space-between' }}>
            <div>{renderZone(row.L, 'L', src.L, ri)}</div>
            <div style={{ textAlign: 'right' }}>{renderZone(row.R, 'R', src.R, ri)}</div>
          </div>
        );
      })}
      {ruleBelow && rule}
      {slotMenu && (
        <TokenSlotMenu
          at={{ x: slotMenu.x, y: slotMenu.y }}
          label={slotMenu.label}
          items={slotMenu.items}
          onClose={() => setSlotMenu(null)}
        />
      )}
    </div>
  );
}

/** End of section — the closing line the forma prints.
 *  `onConfigure` restores the classic canvas's click-the-EOS-line-to-edit-it
 *  shortcut (`onEditSpecial('{EndOfSection}')`). Omitted → inert text, exactly
 *  as every read-only caller renders it today. */
export function EndOfSection({
  forma,
  specNumber,
  specName,
  onConfigure,
}: PaperContext & { onConfigure?: () => void }) {
  const sp = (forma?.special || {}) as Record<string, unknown>;
  if (sp.eosEnabled === false) return null;
  const doc = (forma?.docSettings || {}) as Record<string, unknown>;
  const text = (sp.endOfSectionText as string) || 'END OF SECTION';
  const specNumFmt = formatSpecNumber(specNumber, doc.specNumberFormat as string | undefined);
  const zs = sp.zoneSpace as { eos?: { before?: number } } | undefined;
  // §3.2 — WYSIWYG IS LAW: these are the same four properties word-engine's
  // eosRPr() emits, read from the same field. If the paper and the .docx
  // disagree about END OF SECTION, that is a bug by definition.
  const eos = sp.eosStyle as
    | { bold?: boolean; italic?: boolean; underline?: boolean; size?: number }
    | undefined;
  const body = text.replace('{SpecNumber}', specNumFmt).replace('{SpecName}', specName || '');
  const style: React.CSSProperties = {
    marginTop: ptToPx(
      zs?.eos?.before ?? (sp.eosSpacing as number) ?? (sp.postBodySpacing as number) ?? 24
    ),
    ...(eos?.bold ? { fontWeight: 700 } : {}),
    ...(eos?.italic ? { fontStyle: 'italic' as const } : {}),
    ...(eos?.underline ? { textDecoration: 'underline' } : {}),
    ...(eos?.size ? { fontSize: ptToPx(eos.size) } : {}),
  };
  if (!onConfigure) {
    return (
      <div className="dsg-eos" style={style}>
        {body}
      </div>
    );
  }
  return (
    <div
      className="dsg-eos dsg-eos-editable"
      role="button"
      tabIndex={0}
      title="Edit the end-of-section line"
      aria-label="End of section — click to edit"
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onConfigure();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onConfigure();
        }
      }}
    >
      {body}
    </div>
  );
}

/** The CSS custom properties that make the sheet the real page. */
export function paperVars(forma: StandardConfig | null): React.CSSProperties {
  const geo = resolvePageGeometry(forma);
  return {
    ['--dsg-page-w' as string]: `${inToPx(geo.pageWidthIn)}px`,
    ['--dsg-margin-t' as string]: `${inToPx(geo.marginTopIn)}px`,
    ['--dsg-margin-b' as string]: `${inToPx(geo.marginBottomIn)}px`,
    ['--dsg-margin-l' as string]: `${inToPx(geo.marginLeftIn)}px`,
    ['--dsg-margin-r' as string]: `${inToPx(geo.marginRightIn)}px`,
    ['--dsg-body-font' as string]: geo.bodyFont,
    ['--dsg-body-size' as string]: `${ptToPx(geo.bodyFontSizePt)}px`,
    ['--dsg-line' as string]: String(geo.lineSpacing),
  };
}
